// MarketPulse.tsx - Full Market Pulse intelligence page
// Route: /market-pulse
// Dark Adversyn style. No raw codes in main UI; diagnostics collapsed.
// TICKER IS IN SHELL (App.tsx) - not rendered here.

import React, { useEffect, useState } from "react";
import { apiV1Base } from '../lib/apiConfig';
// PULSEPERF-002 FIX 2 (ui-safe): strategy-context uses ?lite=true. Movers stays on
// the FULL payload so the Top Tradable Movers panel keeps its Forex section — the
// board projection's combined_mover_ticker structurally excludes FX (see
// DEPLOY-COMBINED-001 §UI). FIX 1 (backend, live) already makes full movers
// non-blocking, so there is no latency cost to keeping the full payload here.
import {
  STRATEGY_LITE_PATH,
  strategyTradableCount,
} from '../lib/marketPulsePageData';
import {
  safeText,
  formatFreshness,
  formatPrice,
  formatChangePct,
  formatPermission,
  formatRiskPosture,
  summarizeStatus,
  filterTradable,
  groupMoversByClass,
  STATUS_COLORS,
  STATUS_LABELS,
  formatUniverseSource,
  formatLastUpdated,
  REGIME_FOOTNOTE,
  type FreshnessLevel,
} from "../lib/marketPulseFormatters";
// [data-health-frontend-v1]
import {
  resolveDataHealth,
  componentHealthLabel,
  buildRowDescription,
  buildStrategyImpactCopy,
} from "../lib/dataHealthCopy";
// phase-fallen-edge-ui-v1 / phase-short-intel-ui-v1 — advisory-only loser
// intelligence and short intelligence surfaces. Both panels guarantee no order
// execution and re-assert advisory-only badges per operator brief.
import FallenEdgePanel from "./FallenEdgePanel";
import ShortIntelligencePanel from "./ShortIntelligencePanel";

function apiBase(): string {
  const host =
    typeof window !== "undefined" ? window.location.hostname : "localhost";
  return apiV1Base();
}

async function fetchJSON(path: string): Promise<unknown> {
  try {
    const r = await fetch(`${apiBase()}${path}`, { cache: "no-store" });
    if (!r.ok) return null;
    const d = await r.json();
    return d?.ok !== undefined ? (d.data ?? d) : d;
  } catch {
    return null;
  }
}

interface Mover extends Record<string, unknown> {
  symbol: string;
  display_name?: string;
  asset_class?: string;
  price?: number | null;
  change_percent?: number | null;
  change_pct?: number | null;
  primary_catalyst?: string | null;
  reason?: string | null;
  strategy_impact?: string | null;
  freshness_status?: string;
  tradable?: boolean;
  is_tradable?: boolean;
  tradeable?: boolean;
  trading_enabled?: boolean;
}

interface ProviderHealth {
  status?: string;
  overall_status?: string;
  failure_reason?: string;
  provider?: string;
  last_success_at?: string;
  reason_code?: string;
  latency_ms?: number | null;
}

interface HealthData {
  status?: string;
  overall?: string;
  overall_label?: string;
  freshness_status?: string;
  provider_health?: Record<string, ProviderHealth>;
  component_status?: Record<string, string>;
  reason_codes?: string[];
  cache_age_seconds?: number;
  degraded_mode?: boolean;
  status_message?: string;
  universe_source?: string
  last_success_at?: string;
}

interface AssetRegimeItem {
  title?: string;
  current_move_context?: string;
  regime_label?: string;
  risk_direction?: string;
  strategy_impact?: string;
  freshness_status?: string;
  representative_tradable_symbols?: string[];
}

interface NewsItem {
  title?: string;
  headline?: string;
  source?: string;
  timestamp?: string;
  impact_level?: string;
  affected_tradable_symbols?: string[];
  summary?: string;
  url?: string;
}

interface NewsPayload {
  items?: NewsItem[];
  data_status?: string;
  provider?: string | null;
  reason_code?: string | null;
  message?: string | null;
}

interface CalendarEvent {
  headline?: string;
  name?: string;
  timestamp?: string;
  impact_level?: string;
  affected_tradable_symbols?: string[];
  freshness_status?: string;
}

interface CalendarPayload {
  economic_events?: CalendarEvent[];
  earnings_events?: CalendarEvent[];
  data_status?: string;
  provider?: string | null;
  reason_code?: string | null;
  message?: string | null;
}

interface MoversGroupMeta {
  data_status?: string;
  provider?: string | null;
  message?: string | null;
  reason_code?: string | null;
}

interface MoversPayload {
  [groupName: string]: Mover[] | Record<string, MoversGroupMeta> | string | undefined;
  groups_meta?: Record<string, MoversGroupMeta>;
  universe_source?: string;
  overall_status?: string;
  status_message?: string;
}

interface StrategyData extends Record<string, unknown> {
  trade_permission_hint?: string;
  trading_permission?: string;
  permission?: string;
  risk_posture?: string;
  posture?: string;
  market_regime?: { label?: string; risk_direction?: string; strategy_impact?: string };
  asset_regime?: AssetRegimeItem[];
  tradable_universe?: Array<Record<string, unknown>>;
  affected_symbols?: string[];
  reason_codes?: string[];
  freshness_status?: string;
  overall_status?: string;
  status_message?: string;
  confidence?: number;
  top_tradable_movers?: Record<string, Mover[]>;
}

// Global badge derivation
function deriveGlobalBadge(health: HealthData | null): { label: string; level: FreshnessLevel } {
  if (!health) return { label: "Unavailable", level: "unavailable" };
  const overall = (health.overall ?? "").toLowerCase();
  if (overall === "live") return { label: "Live", level: "live" };
  if (overall === "partial") return { label: resolveDataHealth("LIMITED").label, level: "partial" };
  if (overall === "degraded") return { label: "Degraded", level: "degraded" };
  if (overall === "unavailable") return { label: "Unavailable", level: "unavailable" };
  // Fallback: derive from forex
  const forexStatus = (health.component_status?.["forex"] ?? "").toLowerCase();
  if (forexStatus === "live") return { label: resolveDataHealth("FALLBACK_ACTIVE").label, level: "partial" };
  return { label: "Unavailable", level: "unavailable" };
}

// UI primitives
function StatusBadge({ level, label }: { level: FreshnessLevel; label?: string }) {
  const badgeClass: Record<FreshnessLevel, string> = {
    live: "bg-green-900/50 text-green-400 border border-green-800",
    partial: "bg-yellow-900/50 text-yellow-400 border border-yellow-800",
    stale: "bg-yellow-900/50 text-yellow-400 border border-yellow-800",
    degraded: "bg-orange-900/50 text-orange-400 border border-orange-800",
    unavailable: "bg-red-900/50 text-red-400 border border-red-800",
  };
  const displayLabel = label ?? STATUS_LABELS[level];
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${badgeClass[level]}`}
    >
      <span
        style={{ width: 5, height: 5, borderRadius: "50%", background: STATUS_COLORS[level], display: "inline-block" }}
      />
      {displayLabel}
    </span>
  );
}

function StatusBadgeFromRaw({ raw }: { raw: unknown }) {
  return <StatusBadge level={summarizeStatus(raw)} />;
}

function Card({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-gray-900 rounded-xl p-5 border border-gray-800 ${className}`}>
      {children}
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-lg font-semibold text-white mb-3">{children}</h3>;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">
      {children}
    </div>
  );
}

// Lead Intelligence Summary
function LeadSummary({
  health,
  strategy,
}: {
  health: HealthData | null;
  strategy: StrategyData | null;
}) {
  // PULSEPERF-002 FIX 2: under ?lite=true the universe array is empty; prefer the
  // backend tradable_universe_count (proven equal to filterTradable(...).length).
  const tradableCount = strategyTradableCount(strategy, filterTradable);

  const permHint =
    strategy?.trading_permission ??
    strategy?.trade_permission_hint ??
    strategy?.permission;
  const permLabel = formatPermission(permHint);
  const { label: badgeLabel, level: overallLevel } = deriveGlobalBadge(health);
  const backendMsg = health?.status_message ?? strategy?.status_message;

  let sentences: string[];
  if (!health && !strategy) {
    sentences = [
      "Market intelligence is currently unavailable. The backend service may be starting up.",
      "Existing strategy rules remain active. No new signals are being generated.",
    ];
  } else if (backendMsg) {
    sentences = [backendMsg];
    if (tradableCount > 0) {
      sentences.unshift(
        `${tradableCount} instrument${tradableCount !== 1 ? "s are" : " is"} currently tradable, primarily in the forex market via OANDA.`
      );
    }
    sentences.push(
      `${permLabel}. Confidence in market intelligence is currently ${
        strategy?.confidence != null
          ? Math.round((strategy.confidence as number) * 100) + "%"
          : "reduced"
      }.`
    );
  } else {
    sentences = [];
    if (tradableCount > 0) {
      sentences.push(
        `${tradableCount} instrument${tradableCount !== 1 ? "s are" : " is"} currently tradable, primarily in the forex market via OANDA.`
      );
    } else {
      sentences.push("No instruments are confirmed tradable at this time.");
    }
    if (overallLevel === "live") {
      sentences.push("All data feeds are live and healthy.");
    } else if (overallLevel === "partial") {
      sentences.push(
        buildStrategyImpactCopy({
          overall: (health?.overall as string) ?? "partial",
          componentStatuses: (health?.component_status ?? {}) as Record<string, unknown>,
          permissionLabel: permLabel,
        }),
      );
    } else {
      sentences.push("Core market intelligence is impaired. Existing rules remain active.");
    }
    sentences.push(
      `${permLabel}. Confidence in market intelligence is currently ${
        strategy?.confidence != null
          ? Math.round((strategy.confidence as number) * 100) + "%"
          : "reduced"
      }.`
    );
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <SectionHeading>Intelligence Summary</SectionHeading>
        <StatusBadge level={overallLevel} label={badgeLabel} />
      </div>
      <div className="space-y-1.5">
        {sentences.map((s, i) => (
          <p key={i} className="text-sm text-gray-300 leading-relaxed m-0">
            {s}
          </p>
        ))}
      </div>
    </Card>
  );
}

// Market Regime Grid
function RegimeCard({ item }: { item: AssetRegimeItem }) {
  // [data-health-frontend-v2]
  const rawFreshness = item.freshness_status;
  const bulletText = (item.current_move_context ?? "").toLowerCase();
  const titleText = (item.title ?? "").toLowerCase();
  // Smart-status derivation: when the backend tags this regime as
  // UNAVAILABLE but the move-context bullet says cached data is
  // available, we honour the cache and render "Cached by quota policy".
  let derivedStatus: string | undefined = rawFreshness;
  const upper = String(rawFreshness ?? "").toUpperCase();
  const looksCached = /cach/i.test(bulletText);
  const looksProxy = /proxy/i.test(bulletText) || titleText.includes("volatility");
  const looksNotConnected = /not connected|provider not configured|not configured/i.test(bulletText);
  if ((upper === "UNAVAILABLE" || upper === "") && looksCached) {
    derivedStatus = "CACHE_BY_QUOTA_POLICY";
  } else if ((upper === "UNAVAILABLE" || upper === "LIMITED") && looksProxy) {
    derivedStatus = "PROXY_ACTIVE";
  } else if ((upper === "UNAVAILABLE") && looksNotConnected) {
    derivedStatus = "OPTIONS_PROVIDER_NOT_CONFIGURED";
  }
  const level = summarizeStatus(derivedStatus ?? item.regime_label ?? "unavailable");
  const copy = resolveDataHealth(derivedStatus);
  const title = item.title ?? "Market";
  const bullets: string[] = [];
  if (item.current_move_context) bullets.push(item.current_move_context);
  // strategy_impact intentionally omitted from per-card bullets:
  // it is the SAME boilerplate on every regime ("Additional context only…").
  // The global REGIME_FOOTNOTE below the grid carries it once.

  return (
    <div className="rounded-xl border border-gray-700 bg-gray-800/70 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-gray-200">{title}</span>
        <StatusBadge level={level} label={copy.label} />
      </div>
      {item.regime_label && (
        <div className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
          {item.regime_label.replace(/_/g, " ")}
        </div>
      )}
      <ul className="space-y-1 m-0 pl-0 list-none">
        {bullets.slice(0, 3).map((b, i) => (
          <li key={i} className="text-xs text-gray-400 leading-snug flex gap-1.5">
            <span className="text-gray-600 mt-0.5">&#8226;</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RegimeGrid({ strategy }: { strategy: StrategyData | null }) {
  const regimes: AssetRegimeItem[] = strategy?.asset_regime ?? [];
  if (regimes.length === 0) return null;
  return (
    <Card>
      <SectionHeading>Market Regime</SectionHeading>
      {/* The duplicate paragraph that used to mirror Intelligence Summary is */}
      {/* removed; the same text already appears in the lead summary above. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {regimes.map((r, i) => (
          <RegimeCard key={i} item={r} />
        ))}
      </div>
      <p className="text-[11px] text-gray-500 italic mt-3 leading-snug">
        {REGIME_FOOTNOTE}
      </p>
    </Card>
  );
}

// Mover rows and sections
function MoverRow({ m }: { m: Mover }) {
  const pct = m.change_pct ?? m.change_percent ?? null;
  const up = pct != null && pct > 0;
  const dn = pct != null && pct < 0;
  // Per-row strategy_impact intentionally omitted: it is the SAME
  // ("Eligible for prioritization…" / "Confirm spread…") on every row.
  const catalyst = safeText(m.primary_catalyst ?? m.reason, "—");

  return (
    <div className="flex items-center justify-between py-2 px-3 hover:bg-gray-800/50 rounded">
      <span className="font-mono text-sm text-white w-20 shrink-0">
        {m.display_name ?? m.symbol}
      </span>
      <span className="text-sm text-gray-300 tabular-nums w-24 shrink-0">
        {formatPrice(m.price)}
      </span>
      <span
        className={`text-sm font-medium w-20 shrink-0 tabular-nums ${
          up ? "text-green-400" : dn ? "text-red-400" : "text-gray-500"
        }`}
      >
        {pct != null ? formatChangePct(pct) : "—"}
      </span>
      <span className="text-xs text-gray-500 flex-1 truncate max-w-xs">
        {catalyst}
      </span>
    </div>
  );
}

const CLASS_ORDER = ["forex", "equity", "etf", "crypto", "commodity"];
const CLASS_LABELS: Record<string, string> = {
  forex: "Forex",
  equity: "Equity",
  equities: "Equity",
  etf: "ETF",
  etfs: "ETF",
  crypto: "Crypto",
  commodity: "Commodity",
  commodities: "Commodity",
};

// Reduce a backend group key to its essence so the heading template
// "Top Tradable {label} Movers" never produces "Movers Movers" or
// "Tradable Tradable". Examples:
//   "Top Tradable Equity Movers"           -> "Equity"
//   "Top Tradable ETF Movers"              -> "ETF"
//   "News-Driven Tradable Movers"          -> "News-Driven"
//   "Volatility-Spike Tradable Movers"     -> "Volatility-Spike"
function cleanGroupLabel(rawKey: string): string {
  let k = (rawKey ?? "").trim();
  k = k.replace(/^top\s+tradable\s+/i, "");
  k = k.replace(/\s+movers$/i, "");
  k = k.replace(/\s+tradable$/i, "");
  k = k.replace(/\s+tradable\s+/gi, " ");
  return k.trim();
}

function MoverSection({
  label,
  items,
  meta,
}: {
  label: string;
  items: Mover[];
  meta?: MoversGroupMeta;
}) {
  const tradable = filterTradable(items)
    .filter((m) => !/vix/i.test(String(m.symbol ?? "")))
    .slice(0, 8);

  const dataStatus = (meta?.data_status ?? "").toLowerCase();
  const isUnavailable = dataStatus === "unavailable";
  const placeholderMsg =
    meta?.message ??
    (label.toLowerCase().includes("etf")
      ? "ETF movers unavailable until equity quote provider is connected."
      : `${label} movers unavailable until ${label.toLowerCase()} quote provider is connected.`);

  return (
    <div className="mb-6 last:mb-0">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-base font-semibold text-white">Top Tradable {label} Movers</h3>
        <span className="text-xs text-gray-500">{tradable.length} instruments</span>
      </div>
      {tradable.length === 0 ? (
        isUnavailable ? (
          <div className="py-6 text-center text-gray-500 text-sm border border-dashed border-gray-700 rounded-lg">
            {placeholderMsg}
          </div>
        ) : (
          <p className="text-sm text-gray-600 italic">
            No tradable {label.toLowerCase()} movers available right now.
          </p>
        )
      ) : (
        <div className="divide-y divide-gray-800">
          {tradable.map((m) => (
            <MoverRow key={m.symbol} m={m} />
          ))}
        </div>
      )}
    </div>
  );
}

function MoversPanel({ movers }: { movers: MoversPayload | null }) {
  const groupsMeta = movers?.groups_meta ?? {};

  // Prefer the explicit named groups when present (preserve backend ordering)
  const namedGroups: Array<{ label: string; items: Mover[]; meta?: MoversGroupMeta; key: string }> = [];
  if (movers) {
    for (const [k, v] of Object.entries(movers)) {
      if (k === "groups_meta" || k === "universe_source" || k === "overall_status" || k === "status_message")
        continue;
      if (!Array.isArray(v)) continue;
      const label = cleanGroupLabel(k);
      namedGroups.push({
        label,
        items: v as Mover[],
        meta: groupsMeta[k],
        key: k,
      });
    }
  }
  if (namedGroups.length > 0) {
    // Order: Equity, ETF, Forex, then anything else
    const order = ["Equity", "ETF", "Forex"];
    namedGroups.sort((a, b) => {
      const ai = order.findIndex((o) => o.toLowerCase() === a.label.toLowerCase());
      const bi = order.findIndex((o) => o.toLowerCase() === b.label.toLowerCase());
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
    return (
      <Card>
        <SectionHeading>Top Tradable Movers</SectionHeading>
        {namedGroups.map((g) => (
          <MoverSection key={g.key} label={g.label} items={g.items} meta={g.meta} />
        ))}
      </Card>
    );
  }

  // Fallback: flatten by asset_class
  const allFlat: Mover[] = [];
  if (movers) {
    for (const v of Object.values(movers)) {
      if (!Array.isArray(v)) continue;
      for (const m of v as Mover[]) {
        if (!allFlat.find((x) => x.symbol === m.symbol)) allFlat.push(m);
      }
    }
  }
  const byClass = groupMoversByClass(allFlat as Record<string, unknown>[]) as Record<string, Mover[]>;
  return (
    <Card>
      <SectionHeading>Top Tradable Movers</SectionHeading>
      {CLASS_ORDER.map((cls) => (
        <MoverSection
          key={cls}
          label={CLASS_LABELS[cls] ?? cls.charAt(0).toUpperCase() + cls.slice(1)}
          items={byClass[cls] ?? []}
        />
      ))}
    </Card>
  );
}

// News & Catalysts
function NewsPanel({ news }: { news: NewsPayload | null }) {
  const status = (news?.data_status ?? "").toLowerCase();
  const isUnavailable = status === "unavailable" || !news;

  return (
    <Card>
      <SectionHeading>News &amp; Catalysts</SectionHeading>
      {isUnavailable ? (
        <div className="space-y-2 text-sm text-gray-400 leading-relaxed">
          <p className="m-0">No live news feed connected yet.</p>
          <p className="m-0">Market Pulse is currently using price action and provider health.</p>
          <p className="m-0">News, earnings, and economic calendar feeds are pending connection.</p>
        </div>
      ) : (news?.items?.length ?? 0) === 0 ? (
        <p className="text-sm text-gray-500 italic">No news catalysts at this time.</p>
      ) : (
        <div className="space-y-3">
          {(news?.items ?? []).map((n, i) => {
            const title = safeText(n.title ?? n.headline, "Untitled");
            const source = safeText(n.source, "");
            const ts = n.timestamp ? formatTimestampShort(n.timestamp) : "";
            const impact = (n.impact_level ?? "info").toLowerCase();
            const symbols = (n.affected_tradable_symbols ?? []).slice(0, 6);
            const summary = safeText(n.summary, "");
            return (
              <div key={i} className="border border-gray-700 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-semibold text-gray-200 text-sm">{title}</span>
                  <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${impactClass(impact)}`}>
                    {impact}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mb-1">
                  {source}{source && ts ? " - " : ""}{ts}
                </div>
                {summary && <p className="text-xs text-gray-400 leading-snug m-0">{summary}</p>}
                {symbols.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    Affects: {symbols.join(", ")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function impactClass(impact: string): string {
  if (impact === "high" || impact === "critical") return "bg-red-900/50 text-red-400";
  if (impact === "medium" || impact === "med") return "bg-yellow-900/50 text-yellow-400";
  return "bg-gray-700/50 text-gray-400";
}

function formatTimestampShort(ts: string): string {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return ts;
  }
}

// Catalyst / Events (calendar)
function CalendarPanel({ calendar }: { calendar: CalendarPayload | null }) {
  const status = (calendar?.data_status ?? "").toLowerCase();
  const isUnavailable = status === "unavailable" || !calendar;
  const events = [
    ...((calendar?.economic_events ?? []) as CalendarEvent[]),
    ...((calendar?.earnings_events ?? []) as CalendarEvent[]),
  ];

  return (
    <Card>
      <SectionHeading>Catalyst / Events</SectionHeading>
      {isUnavailable ? (
        <div className="space-y-2 text-sm text-gray-400 leading-relaxed">
          <p className="m-0">Economic and earnings calendar unavailable.</p>
          <p className="m-0">Trading agents will rely on price action and existing risk rules</p>
          <p className="m-0">until calendar data is connected.</p>
        </div>
      ) : events.length === 0 ? (
        <p className="text-sm text-gray-500 italic">No upcoming events at this time.</p>
      ) : (
        <div className="space-y-2">
          {events.map((e, i) => {
            const headline = safeText(e.headline ?? e.name, "Event");
            const ts = e.timestamp ? formatTimestampShort(e.timestamp) : "";
            const impact = (e.impact_level ?? "info").toLowerCase();
            const symbols = (e.affected_tradable_symbols ?? []).slice(0, 6);
            return (
              <div key={i} className="border border-gray-700 rounded-lg p-3 text-sm">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-semibold text-gray-200">{headline}</span>
                  <span className={`text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${impactClass(impact)}`}>
                    {impact}
                  </span>
                  {e.freshness_status && <StatusBadgeFromRaw raw={e.freshness_status} />}
                </div>
                {ts && <div className="text-xs text-gray-500">{ts}</div>}
                {symbols.length > 0 && (
                  <div className="text-xs text-gray-500">
                    Affects: {symbols.join(", ")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// Strategy Impact
function StrategyPanel({
  strategy,
  health,
}: {
  strategy: StrategyData | null;
  health: HealthData | null;
}) {
  const permHint =
    strategy?.trading_permission ??
    strategy?.trade_permission_hint ??
    strategy?.permission;
  const permLabel = formatPermission(permHint);

  // Risk posture should align with overall_status, not be hardcoded
  const overall = (health?.overall ?? strategy?.overall_status ?? "").toLowerCase();
  let posture: string;
  if (strategy?.risk_posture) {
    posture = formatRiskPosture(strategy.risk_posture);
  } else if (overall === "live") {
    posture = "Normal";
  } else if (overall === "partial") {
    posture = "Reduced";
  } else if (overall === "degraded") {
    posture = "Defensive";
  } else {
    posture = "Defensive";
  }

  const affected = strategy?.affected_symbols ?? [];

  const permColor = permLabel.includes("Allowed")
    ? "#22c55e"
    : permLabel.includes("Blocked")
    ? "#ef4444"
    : permLabel.includes("Caution")
    ? "#f59e0b"
    : "#f97316";

  // Posture color also aligned with overall
  const postureColor =
    posture === "Normal"
      ? "#22c55e"
      : posture === "Reduced"
      ? "#eab308"
      : posture === "Defensive"
      ? "#f97316"
      : "#9ca3af";

  let summary: string;
  if (overall === "live") {
    summary = "Market intelligence is healthy. Strategy rules are running at full confidence.";
  } else if (overall === "partial") {
    summary = buildStrategyImpactCopy({
      overall: "partial",
      componentStatuses: (health?.component_status ?? {}) as Record<string, unknown>,
      permissionLabel: permLabel,
    });
  } else {
    summary =
      "Core market intelligence is impaired. Existing strategy rules remain active. Agents should treat new signals with caution.";
  }

  return (
    <Card>
      <SectionHeading>Strategy Impact</SectionHeading>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Permission</div>
          <div className="text-base font-bold" style={{ color: permColor }}>{permLabel}</div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">Risk Posture</div>
          <div className="text-base font-bold" style={{ color: postureColor }}>{posture}</div>
        </div>
      </div>
      {affected.length > 0 && (
        <div className="mb-3">
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-1.5">Affected Instruments</div>
          <div className="flex flex-wrap gap-1.5">
            {affected.slice(0, 12).map((sym) => (
              <span key={sym} className="text-xs px-2 py-0.5 rounded bg-gray-700 text-gray-300 font-mono">
                {sym}
              </span>
            ))}
            {affected.length > 12 && (
              <span className="text-xs text-gray-500">+{affected.length - 12} more</span>
            )}
          </div>
        </div>
      )}
      <p className="text-sm text-gray-400 leading-relaxed m-0">{summary}</p>
    </Card>
  );
}

// Data Health sidebar
const COMPONENT_DISPLAY_NAMES: Record<string, string> = {
  forex: "Forex",
  equities: "Equities",
  equity: "Equity",
  etf: "ETFs",
  etfs: "ETFs",
  news: "News",
  calendar: "Calendar",
  options: "Options",
  futures: "Futures",
  crypto: "Crypto",
};

function componentDisplayLabel(name: string, level: FreshnessLevel, rawStatus?: unknown): string {
  const n = (name ?? "").toLowerCase();
  const display =
    COMPONENT_DISPLAY_NAMES[n] ??
    (n.length > 0 ? n.charAt(0).toUpperCase() + n.slice(1) : "Unknown");
  // [data-health-frontend-v1]
  // If a richer registry-shaped status string is available, prefer it.
  if (rawStatus !== undefined && rawStatus !== null && rawStatus !== "") {
    return componentHealthLabel(display, rawStatus);
  }
  if (level === "live") return `${display}: Live`;
  if (level === "partial") return `${display}: Limited`;
  if (level === "stale") return `${display}: Limited`;
  if (level === "degraded") return `${display}: Limited`;
  return `${display}: Unavailable`;
}

function levelFromComponentStatus(s: string): FreshnessLevel {
  const v = (s || "").toLowerCase();
  if (v === "live") return "live";
  if (v === "limited") return "stale";
  if (v === "partial") return "partial";
  if (v === "degraded") return "degraded";
  return "unavailable";
}

function HealthCard({ health }: { health: HealthData | null }) {
  const { label: globalLabel, level: overallLevel } = deriveGlobalBadge(health);
  const componentStatus = health?.component_status ?? {};
  const providers = health?.provider_health ? Object.entries(health.provider_health) : [];

  const displayRows: Array<{
    name: string;
    level: FreshnessLevel;
    displayLabel: string;
    rawStatus?: unknown;
  }> = [];

  if (Object.keys(componentStatus).length > 0) {
    for (const [name, st] of Object.entries(componentStatus)) {
      // Skip strategy_context from the sidebar; it's internal
      if (name === "strategy_context") continue;
      const level = levelFromComponentStatus(st);
      displayRows.push({ name, level, displayLabel: componentDisplayLabel(name, level, st), rawStatus: st });
    }
  } else {
    for (const [name, ph] of providers) {
      const level = summarizeStatus(ph.overall_status ?? ph.status);
      const phStatus = ph.overall_status ?? ph.status;
      displayRows.push({ name, level, displayLabel: componentDisplayLabel(name, level, phStatus), rawStatus: phStatus });
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <SectionHeading>Data Health</SectionHeading>
        <StatusBadge level={overallLevel} label={globalLabel} />
      </div>
      {displayRows.length > 0 ? (
        <div className="divide-y divide-gray-800">
          {displayRows.map(({ name, displayLabel, level, rawStatus }) => {
            const copy = rawStatus !== undefined ? resolveDataHealth(rawStatus) : null;
            const description = copy && copy.label !== "Live" ? copy.message : null;
            return (
              <div key={name} className="py-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-300">{displayLabel}</span>
                  <StatusBadge level={level} label={copy?.label} />
                </div>
                {description && (
                  <p className="mt-1 text-xs text-gray-500 leading-snug">{description}</p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-gray-500 italic">Health data unavailable.</p>
      )}
      {health?.universe_source && (
        <div className="mt-3 pt-2 border-t border-gray-700 space-y-1">
          <div className="text-xs text-gray-500">
            <span className="text-gray-600">Universe: </span>
            <span className="text-gray-300">{formatUniverseSource(health.universe_source)}</span>
          </div>
          {health?.last_success_at && (
            <div className="text-xs text-gray-500">
              <span className="text-gray-600">Updated: </span>
              <span className="text-gray-300">{formatLastUpdated(health.last_success_at) || "—"}</span>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// Tradable Universe sidebar card
function TradableCard({ strategy }: { strategy: StrategyData | null }) {
  // PULSEPERF-002 FIX 2: count via strategyTradableCount so the lite payload
  // (empty universe array + tradable_universe_count) renders the same number.
  const count = strategyTradableCount(strategy, filterTradable);
  if (count === 0) return null;
  return (
    <Card>
      <SectionLabel>Tradable Universe</SectionLabel>
      <div className="text-3xl font-black text-green-400 mb-1">{count}</div>
      <div className="text-xs text-gray-500">instruments currently tradable</div>
    </Card>
  );
}

// Collapsed Diagnostics
function Diagnostics({
  health,
  strategy,
  movers,
  news,
  calendar,
}: {
  health: HealthData | null;
  strategy: StrategyData | null;
  movers: MoversPayload | null;
  news: NewsPayload | null;
  calendar: CalendarPayload | null;
}) {
  const [open, setOpen] = useState(false);
  const reasonCodes = strategy?.reason_codes ?? health?.reason_codes ?? [];
  const providers = health?.provider_health ? Object.entries(health.provider_health) : [];

  return (
    <div className="mt-6">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-xs text-gray-500 border border-gray-700 rounded-lg px-3 py-1.5 hover:text-gray-300 hover:border-gray-600 transition-colors"
      >
        {open ? "Hide diagnostics" : "Show diagnostics"}
      </button>
      {open && (
        <Card className="mt-3">
          <SectionLabel>Diagnostics (raw)</SectionLabel>
          {health?.cache_age_seconds != null && (
            <div className="text-xs text-gray-500 mb-3">
              Cache age: {formatFreshness(health.cache_age_seconds)}
            </div>
          )}
          {reasonCodes.length > 0 && (
            <div className="mb-4">
              <div className="text-xs text-gray-600 uppercase tracking-wider mb-1.5">Reason Codes</div>
              <div className="flex flex-wrap gap-1.5">
                {reasonCodes.map((rc) => (
                  <span key={rc} className="text-xs px-2 py-0.5 rounded bg-gray-900 text-gray-400 font-mono border border-gray-700">
                    {rc}
                  </span>
                ))}
              </div>
            </div>
          )}
          {providers.length > 0 && (
            <div>
              <div className="text-xs text-gray-600 uppercase tracking-wider mb-1.5">Provider Detail</div>
              {providers.map(([name, ph]) => (
                <div key={name} className="py-2 border-b border-gray-900 last:border-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-semibold text-gray-300 capitalize">
                      {name}{ph.provider ? ` (${ph.provider})` : ""}
                    </span>
                    <StatusBadge level={summarizeStatus(ph.overall_status ?? ph.status)} />
                    {ph.latency_ms != null && (
                      <span className="text-xs text-gray-600">{ph.latency_ms.toFixed(0)}ms</span>
                    )}
                  </div>
                  {ph.failure_reason && (
                    <div className="text-xs text-gray-600 font-mono">{ph.failure_reason}</div>
                  )}
                  {ph.reason_code && (
                    <div className="text-xs text-gray-600 font-mono">{ph.reason_code}</div>
                  )}
                </div>
              ))}
            </div>
          )}
          <div className="mt-4">
            <div className="text-xs text-gray-600 uppercase tracking-wider mb-1.5">Raw JSON</div>
            <pre className="text-xs text-gray-500 bg-gray-900 rounded p-3 overflow-auto max-h-48 border border-gray-800">
              {JSON.stringify({ health, strategy, movers, news, calendar }, null, 2)}
            </pre>
          </div>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rich news item shape from /market-news/feed (multi-provider cascade).
// Keep it tolerant: every field optional so legacy /market-pulse/news still
// works as a fallback.
// ---------------------------------------------------------------------------

interface RichNewsItem {
  headline?: string;
  title?: string;
  summary?: string;
  url?: string;
  source?: string;
  provider?: string;
  published_at?: string;
  symbols?: string[];
  asset_class?: string;
  sentiment?: number | null;
  catalyst_type?: string;
  confidence?: number;
  // Priority 3F (2026-06-10) backend-provided chip metadata.
  freshness_status?: string; // FRESH | RECENT | STALE | UNKNOWN
  relevance_class?: string;  // SYMBOL_SPECIFIC | MULTI_SYMBOL | MACRO | CRYPTO | FOREX
  age_hours?: number | null;
}

interface RichNewsFeed {
  status?: string;
  provider?: string | null;
  source?: string | null;
  last_updated?: string | null;
  age_seconds?: number | null;
  freshness_label?: string;
  display_message?: string | null;
  reason_code?: string | null;
  item_count?: number;
  symbols_covered?: string[];
  items?: RichNewsItem[];
  diagnostics_raw?: Record<string, unknown>;
}

const NEWS_ASSET_TABS: Array<{ id: string; label: string; matcher: (it: RichNewsItem) => boolean }> = [
  { id: "all", label: "All", matcher: () => true },
  { id: "stocks", label: "Stocks", matcher: (it) => (it.asset_class || "").toLowerCase() === "stocks" },
  { id: "crypto", label: "Crypto", matcher: (it) => (it.asset_class || "").toLowerCase() === "crypto" },
  { id: "forex", label: "Forex", matcher: (it) => (it.asset_class || "").toLowerCase() === "forex" },
  { id: "macro", label: "Macro", matcher: (it) => (it.asset_class || "").toLowerCase() === "macro" },
  {
    id: "catalysts",
    label: "Catalysts",
    matcher: (it) => {
      const c = (it.catalyst_type || "").toLowerCase();
      return c !== "" && c !== "unknown" && c !== "general";
    },
  },
];

function NewsHeadlineCard({ item }: { item: RichNewsItem }) {
  const headline = (item.headline || item.title || "").trim();
  if (!headline) return null;
  const source = (item.source || item.provider || "").trim();
  const published = (item.published_at || "").trim();
  const summary = (item.summary || "").trim();
  const symbols = (item.symbols || []).slice(0, 5);
  const catalystType = (item.catalyst_type || "").toLowerCase();
  const url = (item.url || "").trim();

  const catalystColor: Record<string, string> = {
    earnings: "bg-blue-900/50 text-blue-300 border-blue-800",
    macro: "bg-purple-900/50 text-purple-300 border-purple-800",
    analyst: "bg-cyan-900/50 text-cyan-300 border-cyan-800",
    legal: "bg-rose-900/50 text-rose-300 border-rose-800",
    mna: "bg-amber-900/50 text-amber-300 border-amber-800",
    product: "bg-emerald-900/50 text-emerald-300 border-emerald-800",
    market_move: "bg-orange-900/50 text-orange-300 border-orange-800",
  };
  const catalystChip = catalystColor[catalystType] || "";

  // Priority 3F: derive chip presentation from backend-provided fields.
  const provider = (item.provider || "").trim();
  const freshnessRaw = (item.freshness_status || "").toUpperCase();
  const freshnessLabel =
    freshnessRaw === "FRESH"
      ? "FRESH"
      : freshnessRaw === "RECENT"
      ? "RECENT"
      : freshnessRaw === "STALE"
      ? "STALE"
      : freshnessRaw === "UNKNOWN"
      ? ""
      : "";
  const freshnessChipClass =
    freshnessRaw === "FRESH"
      ? "border-emerald-700/60 bg-emerald-900/40 text-emerald-200"
      : freshnessRaw === "RECENT"
      ? "border-yellow-700/60 bg-yellow-900/40 text-yellow-200"
      : freshnessRaw === "STALE"
      ? "border-rose-700/60 bg-rose-900/40 text-rose-200"
      : "border-slate-700/60 bg-slate-800/40 text-slate-300";
  const ageHrs = typeof item.age_hours === "number" ? item.age_hours : null;
  const freshnessTitle =
    ageHrs !== null
      ? `Newest item ${ageHrs.toFixed(1)}h old`
      : "Freshness based on published timestamp.";

  const relevanceRaw = (item.relevance_class || "").toUpperCase();
  const relevanceLabel =
    relevanceRaw === "SYMBOL_SPECIFIC"
      ? "SYMBOL"
      : relevanceRaw === "MULTI_SYMBOL"
      ? "MULTI-SYMBOL"
      : relevanceRaw === "MACRO"
      ? "MACRO"
      : relevanceRaw === "CRYPTO"
      ? "CRYPTO"
      : relevanceRaw === "FOREX"
      ? "FOREX"
      : "";
  const relevanceChipClass =
    relevanceRaw === "SYMBOL_SPECIFIC" || relevanceRaw === "MULTI_SYMBOL"
      ? "border-emerald-700/60 bg-emerald-900/30 text-emerald-200"
      : relevanceRaw === "CRYPTO"
      ? "border-orange-700/60 bg-orange-900/30 text-orange-200"
      : relevanceRaw === "FOREX"
      ? "border-indigo-700/60 bg-indigo-900/30 text-indigo-200"
      : "border-slate-700/60 bg-slate-800/40 text-slate-300";
  const relevanceTitle =
    relevanceRaw === "MACRO"
      ? "Macro / market-wide story. No symbol attribution."
      : relevanceRaw === "SYMBOL_SPECIFIC"
      ? "Symbol-attributed by upstream provider."
      : relevanceRaw === "MULTI_SYMBOL"
      ? "Multiple symbols tagged."
      : "";

  return (
    <article
      className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3 shadow-sm transition-colors hover:border-emerald-500/50 hover:shadow-md focus-within:border-emerald-500/60"
      data-testid="news-headline-card"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-semibold leading-snug text-[var(--app-text)] hover:text-emerald-600 focus:text-emerald-600 focus:outline-none"
            >
              {headline}
            </a>
          ) : (
            <div className="text-sm font-semibold leading-snug text-[var(--app-text)]">{headline}</div>
          )}
          {summary && (
            <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-[var(--muted-text)]">{summary}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest">
            {/* Priority 3F: provider chip — upstream API source. */}
            {provider && (
              <span
                data-testid="news-chip-provider"
                className="rounded border border-sky-700/60 bg-sky-900/40 px-1.5 py-0.5 font-bold text-sky-200"
              >
                {provider.toUpperCase()}
              </span>
            )}
            {/* Publisher / outlet text — kept separate from provider. */}
            {source && source.toLowerCase() !== (provider || "").toLowerCase() && (
              <span className="font-bold text-[var(--muted-text)]">{source}</span>
            )}
            {published && (
              <span className="text-[var(--muted-text)] opacity-80">· {formatLastUpdated(published) || "recent"}</span>
            )}
            {/* Priority 3F: relevance-class chip. */}
            {relevanceLabel && (
              <span
                data-testid="news-chip-relevance"
                className={`px-1.5 py-0.5 rounded border font-bold ${relevanceChipClass}`}
                title={relevanceTitle}
              >
                {relevanceLabel}
              </span>
            )}
            {/* Symbol chips — only render for symbol-specific stories. */}
            {symbols.length > 0 && (
              <span data-testid="news-chip-symbols" className="flex flex-wrap items-center gap-1">
                {symbols.map((s) => (
                  <span
                    key={s}
                    className="rounded border border-[var(--card-border)] bg-[var(--input-bg)] px-1.5 py-0.5 text-[10px] font-bold normal-case tracking-normal text-[var(--app-text)]"
                  >
                    {s}
                  </span>
                ))}
              </span>
            )}
            {/* Priority 3F: freshness chip — FRESH/RECENT/STALE/UNKNOWN. */}
            {freshnessLabel && (
              <span
                data-testid="news-chip-freshness"
                className={`px-1.5 py-0.5 rounded border font-bold ${freshnessChipClass}`}
                title={freshnessTitle}
              >
                {freshnessLabel}
              </span>
            )}
            {catalystType && catalystType !== "unknown" && catalystType !== "general" && catalystChip && (
              <span className={`px-1.5 py-0.5 rounded border ${catalystChip} font-bold`}>
                {catalystType.toUpperCase().replace("_", " ")}
              </span>
            )}
          </div>
        </div>
      </div>
    </article>
  );
}


// Priority 3G Addendum (2026-06-10) — Market Pulse "Show More" pagination.
// Renders below the existing news list. Calls /api/v1/market-pulse/news
// with ?expanded=true to surface beyond the 50-item customer-feed sidecar.
// Build/preview only — does not depend on / mutate live deployed bundle.
function MarketPulseShowMore() {
  const [items, setItems] = React.useState<RichNewsItem[]>([]);
  const [total, setTotal] = React.useState<number | null>(null);
  const [providerCounts, setProviderCounts] = React.useState<Record<string, number>>({});
  const [symbolTagged, setSymbolTagged] = React.useState<number | null>(null);
  const [macroCount, setMacroCount] = React.useState<number | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const PAGE_SIZE = 50;

  // Boot: read total_available_count from a no-op call to the default route.
  React.useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${apiBase()}/market-pulse/news`);
        const env = await r.json();
        const d = env?.data || {};
        if (typeof d.total_available_count === "number") setTotal(d.total_available_count);
        if (d.provider_counts) setProviderCounts(d.provider_counts);
        if (typeof d.symbol_tagged_count === "number") setSymbolTagged(d.symbol_tagged_count);
        if (typeof d.macro_count === "number") setMacroCount(d.macro_count);
      } catch (_e) { /* honest empty fallback */ }
    })();
  }, []);

  const loadNext = async () => {
    setLoading(true);
    setErr(null);
    try {
      const offset = items.length;
      const r = await fetch(`${apiBase()}/market-pulse/news?expanded=true&limit=${PAGE_SIZE}&offset=${offset}`);
      const env = await r.json();
      const d = env?.data || {};
      const next: RichNewsItem[] = Array.isArray(d.items) ? d.items : [];
      setItems((prev) => [...prev, ...next]);
      if (typeof d.total_available_count === "number") setTotal(d.total_available_count);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  const baseDisplayed = 50; // legacy 50 from the sidecar are shown above
  const expandedDisplayed = items.length;
  const totalDisplayed = baseDisplayed + expandedDisplayed;
  const canLoadMore = total !== null && totalDisplayed < total;

  return (
    <section data-testid="market-pulse-show-more" className="mt-4 rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-xs text-[var(--muted-text)]">
          <span data-testid="news-showing-of" className="font-bold uppercase tracking-widest text-[var(--app-text)]">
            {total !== null
              ? `Showing ${Math.min(totalDisplayed, total)} of ${total} collected items`
              : `Showing ${totalDisplayed} items`}
          </span>
          {(symbolTagged !== null || macroCount !== null) && (
            <span className="ml-3 normal-case tracking-normal">
              · {symbolTagged ?? 0} symbol-tagged · {macroCount ?? 0} macro
            </span>
          )}
        </div>
        <button
          type="button"
          data-testid="news-show-more"
          onClick={loadNext}
          disabled={loading || !canLoadMore}
          className="rounded border border-emerald-700/60 bg-emerald-900/30 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-emerald-200 hover:bg-emerald-900/50 disabled:opacity-50"
        >
          {loading ? "Loading…" : canLoadMore ? `Show more (${PAGE_SIZE})` : "No more"}
        </button>
      </div>
      {Object.keys(providerCounts).length > 0 && (
        <div data-testid="news-provider-counts" className="mt-2 flex flex-wrap gap-2 text-[10px] uppercase tracking-widest text-[var(--muted-text)]">
          {Object.entries(providerCounts).map(([prov, cnt]) => (
            <span key={prov} className="rounded border border-sky-700/60 bg-sky-900/30 px-1.5 py-0.5 font-bold text-sky-200">
              {prov.toUpperCase()} · {cnt as number}
            </span>
          ))}
        </div>
      )}
      {err && (
        <div className="mt-2 text-xs text-rose-400">Show-more error: {err}</div>
      )}
      {items.length > 0 && (
        <div data-testid="news-show-more-list" className="mt-3 space-y-2">
          {items.map((it, idx) => (
            <NewsHeadlineCard key={`${idx}-${it.url || it.headline}`} item={it} />
          ))}
        </div>
      )}
    </section>
  );
}

function MiniNav() {
  const sections = [
    { id: "pulse-overview", label: "Overview" },
    { id: "pulse-news", label: "News" },
    { id: "pulse-movers", label: "Movers" },
    { id: "pulse-catalysts", label: "Catalysts" },
    { id: "pulse-fallen-edge", label: "Fallen Edge" },
    { id: "pulse-short-intel", label: "Short Intel" },
    { id: "pulse-data-health", label: "Data Health" },
  ];
  return (
    <nav
      className="sticky top-0 z-10 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-2 bg-gray-950/95 backdrop-blur border-b border-gray-800"
      data-testid="market-pulse-mininav"
    >
      <ul className="flex flex-wrap items-center gap-1 text-[11px] font-bold uppercase tracking-widest">
        {sections.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              className="inline-block px-3 py-1.5 rounded-md text-gray-400 hover:text-emerald-400 hover:bg-gray-900"
            >
              {s.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

// ============================================================================
// TopTradableMoversTicker — above-the-fold category-based mover cards.
//
// Distinct from the shell `MarketPulseTicker` (which is the auto-scrolling
// marquee in App.tsx). This component is a STATIONARY card with:
//   - Category chips (Stocks / ETFs / Forex / News-Driven / Volatility)
//   - Top-10 per category (only categories that actually have real data)
//   - "Updated [time] ET" freshness label per category
//   - "View all movers" button that anchors to the detailed #pulse-movers section
//
// Source: /api/v1/market-pulse/movers (same payload the detailed MoversPanel
// uses below — guaranteed not to fabricate). Browser refetch every 60 s.
// ============================================================================

interface TickerCategory {
  id: string;
  key: string;          // backend key in `top_tradable_movers` / payload top-level
  label: string;
}

const TICKER_CATEGORIES: TickerCategory[] = [
  { id: "stocks",   key: "Top Tradable Equity Movers",       label: "Stocks" },
  { id: "etfs",     key: "Top Tradable ETF Movers",          label: "ETFs" },
  { id: "forex",    key: "Top Tradable Forex Movers",        label: "Forex" },
  { id: "crypto",   key: "Top Tradable Crypto Movers",       label: "Crypto" },
  { id: "news",     key: "News-Driven Tradable Movers",      label: "News-Driven" },
  { id: "vol",      key: "Volatility-Spike Tradable Movers", label: "Volatility" },
];

interface MoversTickerItem extends Mover {
  display_name?: string;
  primary_catalyst?: string | null;
  direction?: string;
  timestamp?: string;
}

function _extractCategoryItems(
  movers: MoversPayload | null,
  cat: TickerCategory,
): MoversTickerItem[] {
  if (!movers) return [];
  const direct = movers[cat.key];
  let items: unknown[] = Array.isArray(direct) ? (direct as unknown[]) : [];
  // Also tolerate a nested `top_tradable_movers` shape some payloads use.
  const nested = (movers as unknown as Record<string, unknown>)["top_tradable_movers"];
  if (!items.length && nested && typeof nested === "object") {
    const inner = (nested as Record<string, unknown>)[cat.key];
    if (Array.isArray(inner)) items = inner as unknown[];
  }
  return (items as MoversTickerItem[]).filter((it) => !!it && typeof it === "object");
}

function _categoryMeta(
  movers: MoversPayload | null,
  cat: TickerCategory,
): MoversGroupMeta | null {
  const groups = movers?.groups_meta || ({} as Record<string, MoversGroupMeta>);
  return (groups as Record<string, MoversGroupMeta>)[cat.key] ?? null;
}

function _itemPrice(it: MoversTickerItem): number | null {
  const p = (it as Record<string, unknown>)["price"];
  const n = typeof p === "number" ? p : Number(p);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function _itemChangePct(it: MoversTickerItem): number | null {
  const cp = (it as Record<string, unknown>)["change_percent"]
          ?? (it as Record<string, unknown>)["change_pct"];
  const n = typeof cp === "number" ? cp : Number(cp);
  return Number.isFinite(n) ? n : null;
}

function TopTradableMoversTicker({ movers }: { movers: MoversPayload | null }) {
  // Slim, single-row, auto-scrolling marquee that sequences real categories
  // back-to-back with category-label separators. Designed to take ~46px
  // vertical so it doesn't push Market News below the fold.
  //
  // Pauses on hover. Real categories only (categories with 0 real items
  // are hidden). Top 8 per category. Anchors items to #pulse-movers.
  const [paused, setPaused] = useState(false);

  // Build the ordered list of items with category-separator markers inline.
  const available = TICKER_CATEGORIES.filter((cat) =>
    _extractCategoryItems(movers, cat).length > 0
  );

  type FlatRow =
    | { kind: "label"; id: string; label: string }
    | { kind: "item"; id: string; sym: string; price: number | null;
        changePct: number | null; positive: boolean };

  const rows: FlatRow[] = [];
  let mostRecentLastUpdated: string | undefined;
  for (const cat of available) {
    rows.push({ kind: "label", id: `label-${cat.id}`, label: cat.label });
    const items = _extractCategoryItems(movers, cat).slice(0, 8);
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const sym = String((it.symbol || "")).trim().toUpperCase();
      if (!sym) continue;
      const price = _itemPrice(it);
      const changePct = _itemChangePct(it);
      const direction = String(it.direction || (changePct !== null && changePct >= 0 ? "up" : "down")).toLowerCase();
      const positive = direction === "up" || (changePct !== null && changePct >= 0);
      rows.push({ kind: "item", id: `${cat.id}-${sym}-${i}`, sym, price, changePct, positive });
    }
    const meta = _categoryMeta(movers, cat);
    const lu = (meta as Record<string, unknown> | null)?.last_updated as string | undefined;
    if (lu && (!mostRecentLastUpdated || lu > mostRecentLastUpdated)) {
      mostRecentLastUpdated = lu;
    }
  }

  // Aggregate freshness across categories (worst-of among available).
  const worst: FreshnessLevel = (() => {
    if (available.length === 0) return "unavailable";
    let level: FreshnessLevel = "live";
    const order: Record<FreshnessLevel, number> = {
      live: 0, partial: 1, stale: 2, degraded: 3, unavailable: 4,
    };
    for (const cat of available) {
      const meta = _categoryMeta(movers, cat);
      const ds = (meta?.data_status || "").toLowerCase() || "unavailable";
      const lvl = summarizeStatus(ds);
      if (order[lvl] > order[level]) level = lvl;
    }
    return level;
  })();

  const empty = rows.length === 0;

  return (
    <section id="pulse-movers-ticker" className="scroll-mt-16" data-testid="top-movers-ticker">
      <div
        className="rounded-lg border border-gray-800 bg-gradient-to-r from-gray-950 via-gray-900 to-gray-950 overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="flex items-stretch min-h-[44px]">
          {/* Sticky label on the left */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-950/80 border-r border-gray-800 flex-shrink-0">
            <span className="text-[10px] font-black uppercase tracking-widest text-emerald-300">
              ◆ Top Tradable Movers
            </span>
            <StatusBadge level={worst} />
            {mostRecentLastUpdated && (
              <span
                className="hidden sm:inline text-[9px] uppercase tracking-widest text-gray-500"
                data-testid="movers-ticker-updated"
              >
                Updated {formatLastUpdated(mostRecentLastUpdated) || "recent"}
              </span>
            )}
          </div>

          {/* Marquee / scrolling row */}
          <div className="flex-1 relative overflow-hidden">
            {empty ? (
              <div
                className="flex items-center h-full px-3 text-[11px] text-gray-500 italic"
                data-testid="movers-ticker-empty"
              >
                No mover categories returned real data this cycle. Adversyn never fabricates movers.
              </div>
            ) : (
              <div
                className="flex items-center gap-6 whitespace-nowrap py-2 px-3 movers-ticker-marquee"
                style={{
                  animation: paused ? "none" : "moversMarquee 10s linear infinite",
                }}
                data-testid="movers-ticker-strip"
              >
                {/* Flat doubled track so translateX(-50%) makes a perfect seamless loop and every item is guaranteed to scroll fully into view each cycle. */}
                {[...rows, ...rows].map((row, idx) => {
                  const cycle = idx >= rows.length ? 1 : 0;
                  if (row.kind === "label") {
                    return (
                      <span
                        key={`${cycle}-${row.id}`}
                        className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-emerald-300 bg-emerald-500/10 border border-emerald-800/60 rounded px-2 py-0.5"
                        data-testid={`movers-ticker-cat-${row.id.replace("label-", "")}`}
                      >
                        ◆ {row.label}
                      </span>
                    );
                  }
                  return (
                    <a
                      key={`${cycle}-${row.id}`}
                      href="#pulse-movers"
                      data-testid="movers-ticker-item"
                      className="flex items-baseline gap-1.5 hover:text-emerald-300 no-underline"
                    >
                      <span className="text-[11px] font-black text-white">{row.sym}</span>
                      <span className="text-[11px] font-bold text-gray-400">
                        {formatPrice(row.price)}
                      </span>
                      <span
                        className={`text-[11px] font-black ${
                          row.positive ? "text-emerald-400" : "text-rose-400"
                        }`}
                      >
                        {row.positive ? "▲" : "▼"} {formatChangePct(row.changePct)}
                      </span>
                    </a>
                  );
                })}
              </div>
            )}

            {/* Edge fades (no horizontal scrollbar) */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-gray-950 to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-gray-950 to-transparent" />
          </div>

          {/* View all anchor */}
          <a
            href="#pulse-movers"
            data-testid="movers-ticker-view-all"
            className="flex-shrink-0 flex items-center gap-1 px-3 py-2 bg-gray-950/80 border-l border-gray-800 text-[10px] font-black uppercase tracking-widest text-emerald-300 hover:bg-emerald-500/10"
          >
            View all ↓
          </a>
        </div>
      </div>

      {/* Keyframes for the marquee. Inline so this slim ticker stays self-contained. */}
      <style>{`
        @keyframes moversMarquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}


function MarketNewsSection() {
  const [feed, setFeed] = useState<RichNewsFeed | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [activeTab, setActiveTab] = useState<string>("all");
  const [showAll, setShowAll] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await fetch(`${apiBase()}/market-news/feed`, { cache: "no-store" });
        if (!r.ok) {
          if (!cancelled) {
            setError(`HTTP ${r.status}`);
            setLoading(false);
          }
          return;
        }
        const body = await r.json();
        if (cancelled) return;
        const payload = (body?.data ?? body) as RichNewsFeed;
        setFeed(payload);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(String((e as Error)?.message || e));
        setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const status = (feed?.status || "UNAVAILABLE").toUpperCase();
  const items = feed?.items || [];
  const tabMatcher = NEWS_ASSET_TABS.find((t) => t.id === activeTab)?.matcher || (() => true);
  const searchLower = search.trim().toLowerCase();
  const filtered = items.filter((it) => {
    if (!tabMatcher(it)) return false;
    if (!searchLower) return true;
    const hay = [
      it.headline || it.title || "",
      it.summary || "",
      it.source || "",
      it.provider || "",
      ...((it.symbols || []) as string[]),
    ].join(" ").toLowerCase();
    return hay.includes(searchLower);
  });
  const DEFAULT_COUNT = 10;
  const visible = showAll ? filtered : filtered.slice(0, DEFAULT_COUNT);

  const level = summarizeStatus(status);
  const totalItemCount = feed?.item_count ?? items.length;
  const symbolsCount = (feed?.symbols_covered || []).length;
  const lastUpdated = feed?.last_updated;
  const source = feed?.source || feed?.provider || "";
  const diagnostics = (feed?.diagnostics_raw || {}) as { providers_succeeded?: string[]; providers_failed?: string[] };
  const successCount = (diagnostics.providers_succeeded || []).length;
  const failedCount = (diagnostics.providers_failed || []).length;

  return (
    <section id="pulse-news" className="scroll-mt-16" data-testid="market-news-section">
      <Card className="bg-gradient-to-br from-gray-900 to-gray-950">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
          <div>
            <h2 className="text-2xl font-black uppercase italic tracking-tight text-white m-0 font-display">
              Market News
            </h2>
            <p className="m-0 text-xs text-gray-500 mt-1">
              Real, multi-provider headlines. No fabricated content.
            </p>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-1">
            <StatusBadge level={level} />
            {source && (
              <span className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">
                {source}
              </span>
            )}
            {lastUpdated && (
              <span className="text-[10px] uppercase tracking-widest text-gray-500">
                Updated {formatLastUpdated(lastUpdated) || "recent"}
              </span>
            )}
          </div>
        </div>

        {/* Stats strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <div className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2">
            <div className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">Articles</div>
            <div className="text-lg font-black text-white">{totalItemCount}</div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2">
            <div className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">Symbols</div>
            <div className="text-lg font-black text-white">{symbolsCount}</div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2">
            <div className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">Providers Live</div>
            <div className="text-lg font-black text-emerald-400">{successCount}</div>
          </div>
          <div className="bg-gray-900 border border-gray-800 rounded-lg px-3 py-2">
            <div className="text-[9px] uppercase tracking-widest text-gray-500 font-bold">Providers Failed</div>
            <div className={`text-lg font-black ${failedCount > 0 ? "text-amber-400" : "text-gray-500"}`}>
              {failedCount}
            </div>
          </div>
        </div>

        {/* Filters + search */}
        <div className="flex flex-col gap-3 mb-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-1">
            {NEWS_ASSET_TABS.map((tab) => {
              const count = items.filter(tab.matcher).length;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => { setActiveTab(tab.id); setShowAll(false); }}
                  data-testid={`news-tab-${tab.id}`}
                  className={`px-3 py-1.5 rounded-md text-[10px] font-black uppercase tracking-widest border transition-colors ${
                    isActive
                      ? "bg-emerald-500/20 text-emerald-300 border-emerald-700"
                      : "bg-gray-900 text-gray-400 border-gray-800 hover:text-gray-200 hover:border-gray-700"
                  }`}
                >
                  {tab.label}
                  <span className="ml-1.5 opacity-60">{count}</span>
                </button>
              );
            })}
          </div>
          <div className="lg:max-w-sm w-full">
            <input
              type="search"
              data-testid="news-search-input"
              placeholder="Search news by symbol or keyword"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setShowAll(false); }}
              className="w-full bg-gray-900 border border-gray-800 rounded-md px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-emerald-700"
            />
          </div>
        </div>

        {/* Body */}
        {loading && (
          <div className="text-gray-500 text-sm py-6" data-testid="news-loading">Loading market news...</div>
        )}
        {!loading && error && (
          <div className="text-amber-400 text-sm py-4" data-testid="news-error">
            News feed temporarily unavailable. Falling back to last known status.
          </div>
        )}
        {!loading && !error && status === "UNAVAILABLE" && (
          <div className="rounded-lg border border-dashed border-gray-800 bg-gray-950/50 p-4 text-sm text-gray-400" data-testid="news-unavailable">
            <div className="text-gray-200 font-bold">
              {feed?.display_message || "No verified market news provider is connected."}
            </div>
            <p className="m-0 mt-1 text-xs italic">
              Adversyn never fabricates headlines. Real items will appear once a provider connects.
            </p>
          </div>
        )}
        {!loading && !error && status !== "UNAVAILABLE" && filtered.length === 0 && (
          <div className="rounded-lg border border-dashed border-gray-800 bg-gray-950/50 p-4 text-sm text-gray-400" data-testid="news-empty-filter">
            No headlines match the current filter or search. Try a different tab or clear the search box.
          </div>
        )}
        {!loading && !error && visible.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="news-grid">
            {visible.map((item, i) => (
              <NewsHeadlineCard key={(item.url || "") + i} item={item} />
            ))}
          </div>
        )}

        {/* Show more / view full */}
        {!loading && !error && filtered.length > DEFAULT_COUNT && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="text-[11px] text-gray-500 uppercase tracking-widest font-bold">
              Showing {Math.min(visible.length, filtered.length)} of {filtered.length}
              {activeTab !== "all" && ` in ${activeTab}`}
            </div>
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid="news-show-more"
              className="px-4 py-2 rounded-md text-xs font-black uppercase tracking-widest border border-emerald-800 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
            >
              {showAll ? "Show fewer" : `Show all ${filtered.length}`}
            </button>
          </div>
        )}
      </Card>
              <MarketPulseShowMore />
          </section>
  );
}

// PULSEPERF-002 FIX 3: module-level SWR snapshot. Tab-switch remounts render
// instantly from the last payloads and refresh in the background — the page is
// never gated behind a single Promise.all, and each resource lands independently.
type MPSnapshot = {
  health: HealthData | null;
  movers: MoversPayload | null;
  strategy: StrategyData | null;
  news: NewsPayload | null;
  calendar: CalendarPayload | null;
  at: number;
};
const _mpCache: { snap: MPSnapshot | null } = { snap: null };
const MP_FRESH_MS = 30_000;

// Main page
export default function MarketPulsePage() {
  const cached = _mpCache.snap;
  const [health, setHealth] = useState<HealthData | null>(cached?.health ?? null);
  const [movers, setMovers] = useState<MoversPayload | null>(cached?.movers ?? null);
  const [strategy, setStrategy] = useState<StrategyData | null>(cached?.strategy ?? null);
  const [news, setNews] = useState<NewsPayload | null>(cached?.news ?? null);
  const [calendar, setCalendar] = useState<CalendarPayload | null>(cached?.calendar ?? null);
  // Per-resource "in-flight with nothing cached yet" flags → per-panel hints.
  const [pending, setPending] = useState<Record<string, boolean>>(() => ({
    health: !cached, movers: !cached, strategy: !cached, news: !cached, calendar: !cached,
  }));

  useEffect(() => {
    let cancelled = false;
    const latest: MPSnapshot = {
      health: cached?.health ?? null, movers: cached?.movers ?? null,
      strategy: cached?.strategy ?? null, news: cached?.news ?? null,
      calendar: cached?.calendar ?? null, at: Date.now(),
    };
    const commit = (key: keyof MPSnapshot, val: any, setter: (v: any) => void) => {
      if (cancelled) return;
      (latest as Record<string, unknown>)[key as string] = val;
      latest.at = Date.now();
      setter(val);
      setPending((p) => (p[key as string] ? { ...p, [key as string]: false } : p));
      _mpCache.snap = { ...latest };
    };
    const load = (key: keyof MPSnapshot, path: string, setter: (v: any) => void,
                  transform?: (v: any) => any) =>
      fetchJSON(path).then((v) => commit(key, transform ? transform(v) : v, setter));

    const freshEnough = cached && (Date.now() - cached.at) < MP_FRESH_MS;
    if (!freshEnough) {
      // Fire independently — NO Promise.all gate. A slow movers no longer blanks
      // the page; each panel updates the instant its own fetch resolves.
      // FIX 2 (ui-safe): movers stays on the FULL payload (keeps the Forex
      // section); strategy-context via ?lite=true.
      load("health", "/market-pulse/health", setHealth);
      load("movers", "/market-pulse/movers", setMovers);
      load("strategy", STRATEGY_LITE_PATH, setStrategy);
      load("news", "/market-pulse/news", setNews);
      load("calendar", "/market-pulse/calendar", setCalendar);
    } else {
      // Fresh cached snapshot (recent tab switch) → show it immediately, no refetch.
      setPending({ health: false, movers: false, strategy: false, news: false, calendar: false });
    }
    // Movers refresh poll (full payload) keeps the ticker/panel live.
    const moversTimer = setInterval(() => {
      fetchJSON("/market-pulse/movers").then((m) => {
        if (!cancelled && m) commit("movers", m, setMovers);
      });
    }, 60_000);
    return () => { cancelled = true; clearInterval(moversTimer); };
  }, []);

  const { label: globalLabel, level: overallLevel } = deriveGlobalBadge(health);

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6 text-gray-200">
      {/* NOTE: The ticker strip lives in App.tsx shell (MarketPulseTicker).
           It is NOT rendered here to avoid duplicates. */}

      <MiniNav />

      {/* Page header */}
      <div id="pulse-overview" className="scroll-mt-16">
        <div className="flex items-center gap-3 mb-1">
          <h1 className="text-4xl font-black uppercase italic tracking-tight font-display m-0 text-white">
            Market Pulse
          </h1>
          {health && <StatusBadge level={overallLevel} label={globalLabel} />}
        </div>
        <p className="text-sm text-gray-500 font-medium italic m-0">
          Live intelligence for the Adversyn trading system
        </p>
      </div>

      {/* PULSEPERF-002 FIX 3: no full-page loading gate. The page shell + every
          panel render immediately; each panel shows its own loading/empty/error
          state as its independent fetch resolves. */}
      {(
        <>
          {/* Above-the-fold Top Tradable Movers ticker (stationary card with
              category chips and per-item cards). Visually distinct from the
              shell MarketPulseTicker marquee in App.tsx. */}
          {!movers && pending.movers ? (
            <div data-testid="movers-loading" className="text-gray-500 text-sm py-3">
              Loading movers…
            </div>
          ) : (
            <TopTradableMoversTicker movers={movers} />
          )}

          {/* News section pulled to the top, above the long tables, so the
              user immediately sees real headlines and the data-quality badge. */}
          <MarketNewsSection />

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Left/main: spans 2 cols (~65%) */}
            <div className="xl:col-span-2 space-y-6">
              <LeadSummary health={health} strategy={strategy} />
              <RegimeGrid strategy={strategy} />
              <section id="pulse-movers" className="scroll-mt-16">
                <details open className="bg-gray-900 border border-gray-800 rounded-xl">
                  <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between hover:bg-gray-850">
                    <span className="text-lg font-semibold text-white">Top Tradable Movers</span>
                    <span className="text-[10px] text-gray-500 uppercase tracking-widest">click to collapse</span>
                  </summary>
                  <div className="px-5 pb-5">
                    <MoversPanel movers={movers} />
                  </div>
                </details>
              </section>
              <section id="pulse-catalysts" className="scroll-mt-16">
                <details className="bg-gray-900 border border-gray-800 rounded-xl">
                  <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between hover:bg-gray-850">
                    <span className="text-lg font-semibold text-white">Catalyst / Events Calendar</span>
                    <span className="text-[10px] text-gray-500 uppercase tracking-widest">click to expand</span>
                  </summary>
                  <div className="px-5 pb-5 space-y-5">
                    <NewsPanel news={news} />
                    <CalendarPanel calendar={calendar} />
                  </div>
                </details>
              </section>

              {/* phase-fallen-edge-ui-v1 — Loser intelligence (advisory-only). */}
              <section id="pulse-fallen-edge" className="scroll-mt-16">
                <FallenEdgePanel />
              </section>

              {/* phase-short-intel-ui-v1 — Short intelligence (advisory-only). */}
              <section id="pulse-short-intel" className="scroll-mt-16">
                <ShortIntelligencePanel />
              </section>
            </div>

            {/* Right rail: spans 1 col (~35%) */}
            <div className="xl:col-span-1 space-y-6">
              <section id="pulse-data-health" className="scroll-mt-16 space-y-6">
                <HealthCard health={health} />
                <StrategyPanel strategy={strategy} health={health} />
                <TradableCard strategy={strategy} />
                <Diagnostics
                  health={health}
                  strategy={strategy}
                  movers={movers}
                  news={news}
                  calendar={calendar}
                />
              </section>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
